import { siteConfig } from '../config/siteConfig';
import { fetchWithRetry } from '../utils/fetchWithRetry';
import { LOCALE_STORAGE_KEY, messages } from '../i18n';

function getLocale() {
  const stored = localStorage.getItem(LOCALE_STORAGE_KEY);
  return stored === 'sv' || stored === 'en' ? stored : siteConfig.defaultLocale;
}

async function handleResponse<T>(response: Response): Promise<T> {
  if (!response.ok) {
    if (response.status === 401) {
      const locale = getLocale();
      throw new Error(messages[locale]['error.mustLogIn']);
    }
    const error = await (response.json() as Promise<{ message: string }>);
    const locale = getLocale();
    throw new Error(
      error.message || messages[locale]['error.somethingWentWrong']
    );
  }
  return response.json() as Promise<T>;
}

export async function handleBlobResponse<T>(response: Response) {
  const blob = await response.blob();

  return blob as T;
}

function createHttpClient(baseURL: string) {
  const fullUrl = (url: string) => `${baseURL}${url}`;

  return {
    get: async <T>(
      url: string,
      responseHandler = handleResponse
    ): Promise<T> => {
      const response = await fetchWithRetry(fullUrl(url), {
        method: 'GET',
        headers: {
          'Accept-Language': getLocale(),
        },
        credentials: 'include',
      });
      return responseHandler<T>(response);
    },
    post: async <T, D>(
      url: string,
      data: D,
      options?: Partial<RequestInit>,
      responseHandler = handleResponse
    ): Promise<T> => {
      const response = await fetchWithRetry(fullUrl(url), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept-Language': getLocale(),
        },
        credentials: 'include',
        body: JSON.stringify(data),
        ...options,
      });
      return responseHandler<T>(response);
    },
    delete: async <T, D>(url: string, data?: D): Promise<T> => {
      const response = await fetchWithRetry(fullUrl(url), {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'Accept-Language': getLocale(),
        },
        credentials: 'include',
        body: data ? JSON.stringify(data) : undefined,
      });
      return handleResponse<T>(response);
    },
    put: async <T, D>(url: string, data: D): Promise<T> => {
      const response = await fetchWithRetry(fullUrl(url), {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Accept-Language': getLocale(),
        },
        credentials: 'include',
        body: JSON.stringify(data),
      });
      return handleResponse<T>(response);
    },
    // Add other methods like put, delete, etc. as needed
  };
}

export default createHttpClient;
